import { useState, useEffect } from "react";

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const STORAGE_KEY = "cookie_consent";
const PREFS_KEY = "cookie_prefs";

function getStoredConsent(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function getStoredPrefs(): CookiePreferences {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  return { necessary: true, analytics: false, marketing: false };
}

function setConsent(value: "accepted" | "rejected" | "custom", prefs?: CookiePreferences) {
  try {
    localStorage.setItem(STORAGE_KEY, value);
    if (prefs) localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // ignore
  }
}

export function useCookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState<CookiePreferences>({ necessary: true, analytics: false, marketing: false });

  useEffect(() => {
    const consent = getStoredConsent();
    if (!consent) {
      setVisible(true);
    } else {
      setPrefs(getStoredPrefs());
    }
  }, []);

  const acceptAll = () => {
    const all: CookiePreferences = { necessary: true, analytics: true, marketing: true };
    setConsent("accepted", all);
    setPrefs(all);
    setVisible(false);
    setShowSettings(false);
  };

  const rejectAll = () => {
    const minimal: CookiePreferences = { necessary: true, analytics: false, marketing: false };
    setConsent("rejected", minimal);
    setPrefs(minimal);
    setVisible(false);
    setShowSettings(false);
  };

  const saveCustom = (newPrefs: CookiePreferences) => {
    setConsent("custom", newPrefs);
    setPrefs(newPrefs);
    setVisible(false);
    setShowSettings(false);
  };

  const openSettings = () => {
    setPrefs(getStoredPrefs());
    setShowSettings(true);
    setVisible(true);
  };

  return { visible, showSettings, prefs, acceptAll, rejectAll, saveCustom, openSettings, setShowSettings };
}

export default function CookieConsent() {
  const { visible, showSettings, prefs, acceptAll, rejectAll, saveCustom, setShowSettings } = useCookieConsent();

  const [draftPrefs, setDraftPrefs] = useState<CookiePreferences>(prefs);

  useEffect(() => {
    setDraftPrefs(prefs);
  }, [prefs, showSettings]);

  if (!visible) {
    return (
      <button
        onClick={() => {
          setDraftPrefs(getStoredPrefs());
          setShowSettings(true);
        }}
        className="fixed bottom-4 left-4 z-50 px-4 py-2 bg-stone-800 text-white text-xs rounded-full shadow-lg hover:bg-stone-700 transition-colors cursor-pointer whitespace-nowrap"
        aria-label="Gérer les cookies"
      >
        <i className="ri-settings-3-line mr-1"></i>
        Gérer les cookies
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Consentement cookies">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300">
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-lg">
              <i className="ri-shield-check-line text-amber-700 text-lg"></i>
            </div>
            <h3 className="text-lg font-bold text-stone-900">
              {showSettings ? "Préférences des cookies" : "Votre vie privée compte"}
            </h3>
          </div>
          <p className="text-sm text-stone-500 leading-relaxed">
            {showSettings
              ? "Personnalisez les types de cookies que vous acceptez. Les cookies nécessaires sont toujours actifs."
              : "Nous utilisons des cookies pour assurer le bon fonctionnement du site. Vous pouvez choisir d'accepter ou de refiner vos préférences."}
          </p>
        </div>

        {/* Settings */}
        {showSettings && (
          <div className="px-6 pb-2 space-y-3">
            <div className="flex items-start justify-between gap-4 p-3 bg-stone-50 rounded-lg">
              <div>
                <p className="text-sm font-semibold text-stone-900">Cookies nécessaires</p>
                <p className="text-xs text-stone-500 mt-0.5">Indispensables au fonctionnement du site (langue, session, consentement).</p>
              </div>
              <div className="w-11 h-6 bg-amber-600 rounded-full flex items-center justify-end px-1 flex-shrink-0">
                <div className="w-4 h-4 bg-white rounded-full"></div>
              </div>
            </div>
            <div className="flex items-start justify-between gap-4 p-3 bg-stone-50 rounded-lg">
              <div>
                <p className="text-sm font-semibold text-stone-900">Cookies analytiques</p>
                <p className="text-xs text-stone-500 mt-0.5">Nous aident à comprendre comment vous utilisez le site (Google Analytics).</p>
              </div>
              <button
                onClick={() => setDraftPrefs((p) => ({ ...p, analytics: !p.analytics }))}
                className={`w-11 h-6 rounded-full flex items-center px-1 flex-shrink-0 transition-colors cursor-pointer ${draftPrefs.analytics ? "bg-amber-600 justify-end" : "bg-stone-300 justify-start"}`}
                aria-label="Activer les cookies analytiques"
              >
                <div className="w-4 h-4 bg-white rounded-full"></div>
              </button>
            </div>
            <div className="flex items-start justify-between gap-4 p-3 bg-stone-50 rounded-lg">
              <div>
                <p className="text-sm font-semibold text-stone-900">Cookies marketing</p>
                <p className="text-xs text-stone-500 mt-0.5">Utilisés pour la publicité et le remarketing (Facebook Pixel, etc.).</p>
              </div>
              <button
                onClick={() => setDraftPrefs((p) => ({ ...p, marketing: !p.marketing }))}
                className={`w-11 h-6 rounded-full flex items-center px-1 flex-shrink-0 transition-colors cursor-pointer ${draftPrefs.marketing ? "bg-amber-600 justify-end" : "bg-stone-300 justify-start"}`}
                aria-label="Activer les cookies marketing"
              >
                <div className="w-4 h-4 bg-white rounded-full"></div>
              </button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="px-6 py-4 flex flex-col sm:flex-row gap-2">
          {showSettings ? (
            <>
              <button
                onClick={() => saveCustom(draftPrefs)}
                className="flex-1 py-2.5 bg-amber-700 text-white text-sm font-medium rounded-full hover:bg-amber-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                Enregistrer mes choix
              </button>
              <button
                onClick={() => setShowSettings(false)}
                className="flex-1 py-2.5 bg-stone-100 text-stone-700 text-sm font-medium rounded-full hover:bg-stone-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                Retour
              </button>
            </>
          ) : (
            <>
              <button
                onClick={acceptAll}
                className="flex-1 py-2.5 bg-amber-700 text-white text-sm font-medium rounded-full hover:bg-amber-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                Tout accepter
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="flex-1 py-2.5 bg-stone-100 text-stone-700 text-sm font-medium rounded-full hover:bg-stone-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                Personnaliser
              </button>
              <button
                onClick={rejectAll}
                className="flex-1 py-2.5 border border-stone-300 text-stone-600 text-sm font-medium rounded-full hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Tout refuser
              </button>
            </>
          )}
        </div>

        {/* Links */}
        {!showSettings && (
          <div className="px-6 pb-5 text-center">
            <a href="/privacy" className="text-xs text-amber-700 hover:text-amber-800 transition-colors">
              En savoir plus dans notre politique de confidentialité
            </a>
          </div>
        )}
      </div>
    </div>
  );
}